import {
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
  HostListener,
  HostBinding,
  NgZone,
} from '@angular/core';
import { Router } from '@angular/router';
import { FormBuilder } from '@angular/forms';
import { OverlapService } from 'src/app/shared/service/overlapService/overlap.service';
import { ToastrService } from 'ngx-toastr';
import { MatSort, Sort } from '@angular/material/sort';
import { CargoheatingCommonService } from 'src/app/shared/service/cargo-heating-service/cargoheating-common.service';
import { RequestFormService } from 'src/app/shared/service/requestForm/request-form.service';
import { Chart } from 'angular-highcharts';
import * as Highcharts from 'highcharts';
import HC_exporting from 'highcharts/modules/exporting';
import HC_exportData from 'highcharts/modules/export-data';
import { DEMO_VESSEL_IMOS } from 'src/app/shared/helpers/heating-table-dynamic-extract.util';

HC_exporting(Highcharts);
HC_exportData(Highcharts);

/** One headline metric in the benchmarking KPI strip, with its Veg Oil / DPP split. */
export interface BenchmarkKpiTile {
  key: string;
  title: string;
  icon: string;
  theme: 'records' | 'cargo' | 'positive' | 'negative';
  unit: string;
  format: string;
  value: number;
  veg: number;
  dpp: number;
  vegPct: number;
  dppPct: number;
  /** Saving as % of benchmark consumption (fuel tile only); null when there is no benchmark. */
  savingPct?: number | null;
  vegSavingPct?: number | null;
  dppSavingPct?: number | null;
}

/**
 * Client colours for the summary charts, in fixed order (never cycled). Validated for
 * colour-vision deficiency on adjacent pairs; dark mode uses the same hues stepped for the
 * dark surface. Clients beyond the eighth share a neutral grey.
 */
const CLIENT_PALETTE_LIGHT = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const CLIENT_PALETTE_DARK = ['#3987e5', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'];
const CLIENT_OVERFLOW_LIGHT = '#94a3b8';
const CLIENT_OVERFLOW_DARK = '#64748b';

type BreakdownKey = 'voyage' | 'fuel' | 'co2';

@Component({
  selector: 'app-benchmarking',
  templateUrl: './benchmarking.component.html',
  styleUrls: ['./benchmarking.component.scss'],
})
export class BenchmarkingComponent implements OnInit, OnDestroy {
  @ViewChild(MatSort) sort!: MatSort;

  benchmarkData: any[] = [];
  /** Drives the skeleton loader for the benchmarking graphs + table. */
  isBenchLoading = true;
  paginatedData: any[] = [];
  currentPage = 0;
  itemsPerPage = 50;
  benchamrkForm: any;
  currentSort: Sort = { active: 'end_date', direction: 'desc' };

  isFilterSubmitted: boolean = false;
  showOnlyCommentedRecords: boolean = false;
  openingBenchmarkReport = false;
  readonly benchmarkReportDisabledTooltip =
    'Please select a client to view PDF Report.';
  showGraphView: boolean = true;
  voyageChart!: Chart;
  fuelSavingChart!: Chart;
  co2SavingChart!: Chart;
  consumptionChart!: Chart;
  totalVoyagesSummary: number = 0;
  totalFuelSummary: number = 0;
  totalCo2Summary: number = 0;
  /** Summary view: the three client breakdown lists scroll together. */
  @ViewChild('voyageScrollContainer')
  set voyageScrollContainerRef(ref: ElementRef<HTMLDivElement> | undefined) {
    this.bindBreakdownList('voyage', ref);
  }
  @ViewChild('fuelScrollContainer')
  set fuelScrollContainerRef(ref: ElementRef<HTMLDivElement> | undefined) {
    this.bindBreakdownList('fuel', ref);
  }
  @ViewChild('co2ScrollContainer')
  set co2ScrollContainerRef(ref: ElementRef<HTMLDivElement> | undefined) {
    this.bindBreakdownList('co2', ref);
  }
  private breakdownLists: Partial<Record<BreakdownKey, HTMLElement>> = {};
  private breakdownUnbinders: Partial<Record<BreakdownKey, () => void>> = {};
  /** The list the user is interacting with; it drives the other two. */
  private activeBreakdown: BreakdownKey | null = null;

  voyageBreakdownList: Array<{ name: string; value: number; percentage: number; color: string }> = [];
  fuelBreakdownList: Array<{ name: string; value: number; percentage: number; color: string }> = [];
  co2BreakdownList: Array<{ name: string; value: number; percentage: number; color: string }> = [];
  Highcharts: typeof Highcharts = Highcharts;
  fullBenchmarkResponse: any = null;
  cargoTypes: string[] = [];

  clientNames: string[] = [];

  vesselNames: string[] = [];

  segmentNames: string[] = [];

  clientSearchQuery: string = '';
  cargoSearchQuery: string = '';
  vesselSearchQuery: string = '';
  segmentSearchQuery: string = '';

  clientDropdownOpen = false;
  cargoDropdownOpen = false;
  vesselDropdownOpen = false;
  segmentDropdownOpen = false;

  selectedClients: string[] = [];
  selectedCargoTypes: string[] = [];
  selectedVessels: string[] = [];
  selectedSegments: string[] = [];

  username = 'Bunge Shipping';

  // Indicates if the logged-in user is an admin/internal user (e.g., Meraki Operations)
  isAdminUser: boolean = false;

  clientTenantMap: any = {
    Alghaf: 'alghaf',
    'Bunge SA': 'bunge',
    'Clearlake Shipping': 'clearlake',
    'Elite Tankships': 'elite',
    Litasco: 'litasco',
    Northwell: 'northwell',
    'Stena Bulk': 'stena',
  };

  totalFuelSavingResult: number = 0;
  totalCo2SavingResult: number = 0;
  totalVoyageConsumption: number = 0;
  totalBenchmarkConsumption: number = 0;
  vegOilBenchmarkConsumption: number = 0;
  dppBenchmarkConsumption: number = 0;
  vegOilFuelSavingResult: number = 0;
  vegOilCo2SavingResult: number = 0;
  vegOilRecordsCount: number = 0;
  dppFuelSavingResult: number = 0;
  dppCo2SavingResult: number = 0;
  dppRecordsCount: number = 0;
  totalCargoVolumeMt: number = 0;
  vegOilCargoVolumeMt: number = 0;
  dppCargoVolumeMt: number = 0;
  allData: any[] = [];
  kpiTiles: BenchmarkKpiTile[] = [];

  showDaysColumns: boolean = false;
  showConsumptionColumns: boolean = false;
  tableSearchQuery: string = '';
  activePreset: 'all' | 'compact' | 'days' | 'consumption' = 'compact';
  loginPic: any;

  isClientLogin = false;
  selectedRemarksRow: {
    vessel_name: string;
    client_name: string;
    voy_no: string;
    start_port: string;
    end_port: string;
    start_date?: string;
    end_date?: string;
    remarks: string | null;
    targetRow: any;
  } | null = null;
  remarksPoints: { id: number; text: string }[] = [];
  showRemarksPopup = false;
  isSavingRemarks = false;

  get tableColumnCount(): number {
    const baseColumns = this.isAdminUser ? 13 : 12;
    const daysColumns = 1 + (this.showDaysColumns ? 3 : 0);
    const consumptionColumns = 4 + (this.showConsumptionColumns ? 6 : 0);
    return baseColumns + daysColumns + consumptionColumns;
  }

  get isBungeUser(): boolean {
    const user = (this.username || localStorage.getItem('username') || '').toLowerCase();
    return user.includes('bunge');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    this.clientDropdownOpen = false;
    this.cargoDropdownOpen = false;
    this.vesselDropdownOpen = false;
    this.segmentDropdownOpen = false;
  }

  constructor(
    private commonService: CargoheatingCommonService,
    private router: Router,
    private fb: FormBuilder,
    private overlayService: OverlapService,
    private toastr: ToastrService,
    private requestFormService: RequestFormService,
    private zone: NgZone,
  ) {
    this.benchamrkForm = this.fb.group({
      cargoType: [''],
      client: [''],
      from_date: [''],
      to_date: [''],
      voyageRunningOrCompleted: [''],
      vessel: [''],
    });
    this.calculateTotals();
  }

  ngOnInit(): void {
    this.loginPic = localStorage.getItem('loginPic');
    const loggedInUser = localStorage.getItem('username');
    this.username = loggedInUser ? loggedInUser.trim() : '';
    this.isAdminUser = this.commonService.isMerakiOperations(this.username);
    this.isClientLogin = this.commonService.isCargoHeatingClientLogin();
    if (!this.isAdminUser) {
      this.showGraphView = false;
    }
    if (this.isBungeUser) {
      this.selectedCargoTypes = ['Veg Oil'];
    }

    this.loadBenchmarkData(true);
  }

  toggleClientDropdown(event?: Event) {
    if (event) event.stopPropagation();
    this.clientDropdownOpen = !this.clientDropdownOpen;
    this.cargoDropdownOpen = false;
    this.vesselDropdownOpen = false;
    this.segmentDropdownOpen = false;
  }

  toggleCargoDropdown(event?: Event) {
    if (this.isBungeUser) return;
    if (event) event.stopPropagation();
    this.cargoDropdownOpen = !this.cargoDropdownOpen;
    this.clientDropdownOpen = false;
    this.vesselDropdownOpen = false;
    this.segmentDropdownOpen = false;
  }

  toggleVesselDropdown(event?: Event) {
    if (event) event.stopPropagation();
    this.vesselDropdownOpen = !this.vesselDropdownOpen;
    this.clientDropdownOpen = false;
    this.cargoDropdownOpen = false;
    this.segmentDropdownOpen = false;
  }

  toggleSegmentDropdown(event?: Event) {
    if (event) event.stopPropagation();
    this.segmentDropdownOpen = !this.segmentDropdownOpen;
    this.clientDropdownOpen = false;
    this.cargoDropdownOpen = false;
    this.vesselDropdownOpen = false;
  }

  // Client logic
  clearSingleFilter(type: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    if (type === 'client') {
      this.selectedClients = [];
      this.clientSearchQuery = '';
    } else if (type === 'cargo') {
      if (!this.isBungeUser) {
        this.selectedCargoTypes = [];
        this.cargoSearchQuery = '';
      }
    } else if (type === 'vessel') {
      this.selectedVessels = [];
      this.vesselSearchQuery = '';
    } else if (type === 'segment') {
      this.selectedSegments = [];
      this.segmentSearchQuery = '';
    }
    this.currentPage = 0;
    this.applyFiltersAndSearch();
  }

  get hasActiveFilters(): boolean {
    return (
      this.selectedClients.length > 0 ||
      (!this.isBungeUser && this.selectedCargoTypes.length > 0) ||
      this.selectedVessels.length > 0 ||
      this.selectedSegments.length > 0 ||
      !!this.benchamrkForm?.controls['from_date']?.value ||
      !!this.benchamrkForm?.controls['to_date']?.value
    );
  }

  clearClientFilter(event?: Event) {
    if (event) event.stopPropagation();
    this.selectedClients = [];
    this.currentPage = 0;
    this.applyFiltersAndSearch();
  }

  getFilteredClients() {
    return this.clientNames.filter((opt: string) =>
      opt.toLowerCase().includes(this.clientSearchQuery.toLowerCase()),
    );
  }

  onClientChange(event: any, client: string) {
    if (event.target.checked) {
      this.selectedClients.push(client);
    } else {
      this.selectedClients = this.selectedClients.filter((c) => c !== client);
    }
    this.currentPage = 0;
    this.applyFiltersAndSearch();
  }

  toggleAllClients(event: any) {
    const filtered = this.getFilteredClients();
    if (event.target.checked) {
      const current = new Set(this.selectedClients);
      filtered.forEach((c) => current.add(c));
      this.selectedClients = Array.from(current);
    } else {
      const filteredSet = new Set(filtered);
      this.selectedClients = this.selectedClients.filter((c) => !filteredSet.has(c));
    }
    this.currentPage = 0;
    this.applyFiltersAndSearch();
  }

  isAllClientsSelected() {
    const filtered = this.getFilteredClients();
    return (
      filtered.length > 0 &&
      filtered.every((c) => this.selectedClients.includes(c))
    );
  }

  // Cargo logic
  clearCargoFilter(event?: Event) {
    if (this.isBungeUser) return;
    if (event) event.stopPropagation();
    this.selectedCargoTypes = [];
    this.currentPage = 0;
    this.applyFiltersAndSearch();
  }

  getFilteredCargos() {
    return this.cargoTypes.filter((opt: string) =>
      opt.toLowerCase().includes(this.cargoSearchQuery.toLowerCase()),
    );
  }

  onCargoChange(event: any, cargo: string) {
    if (this.isBungeUser) return;
    if (event.target.checked) {
      this.selectedCargoTypes.push(cargo);
    } else {
      this.selectedCargoTypes = this.selectedCargoTypes.filter(
        (c) => c !== cargo,
      );
    }
    this.currentPage = 0;
    this.applyFiltersAndSearch();
  }

  toggleAllCargos(event: any) {
    if (this.isBungeUser) return;
    const filtered = this.getFilteredCargos();
    if (event.target.checked) {
      const current = new Set(this.selectedCargoTypes);
      filtered.forEach((c) => current.add(c));
      this.selectedCargoTypes = Array.from(current);
    } else {
      const filteredSet = new Set(filtered);
      this.selectedCargoTypes = this.selectedCargoTypes.filter(
        (c) => !filteredSet.has(c),
      );
    }
    this.currentPage = 0;
    this.applyFiltersAndSearch();
  }

  isAllCargosSelected() {
    const filtered = this.getFilteredCargos();
    return (
      filtered.length > 0 &&
      filtered.every((c) => this.selectedCargoTypes.includes(c))
    );
  }

  // Vessel logic
  clearVesselFilter(event?: Event) {
    if (event) event.stopPropagation();
    this.selectedVessels = [];
    this.currentPage = 0;
    this.applyFiltersAndSearch();
  }

  clearDateFilter(event?: Event) {
    if (event) event.stopPropagation();
    this.benchamrkForm.patchValue({
      from_date: null,
      to_date: null,
    });
    this.currentPage = 0;
    this.applyFiltersAndSearch();
  }

  onDateChange(): void {
    this.currentPage = 0;
    this.applyFiltersAndSearch();
  }

  getFilteredVessels() {
    return this.vesselNames.filter((opt: string) =>
      opt.toLowerCase().includes(this.vesselSearchQuery.toLowerCase()),
    );
  }

  onVesselChange(event: any, vessel: string) {
    if (event.target.checked) {
      this.selectedVessels.push(vessel);
    } else {
      this.selectedVessels = this.selectedVessels.filter((v) => v !== vessel);
    }
    this.currentPage = 0;
    this.applyFiltersAndSearch();
  }

  toggleAllVessels(event: any) {
    const filtered = this.getFilteredVessels();
    if (event.target.checked) {
      const current = new Set(this.selectedVessels);
      filtered.forEach((v) => current.add(v));
      this.selectedVessels = Array.from(current);
    } else {
      const filteredSet = new Set(filtered);
      this.selectedVessels = this.selectedVessels.filter((v) => !filteredSet.has(v));
    }
    this.currentPage = 0;
    this.applyFiltersAndSearch();
  }

  isAllVesselsSelected() {
    const filtered = this.getFilteredVessels();
    return (
      filtered.length > 0 &&
      filtered.every((v) => this.selectedVessels.includes(v))
    );
  }

  // Segment logic
  getFilteredSegments() {
    return this.segmentNames.filter((opt: string) =>
      opt.toLowerCase().includes(this.segmentSearchQuery.toLowerCase()),
    );
  }

  onSegmentChange(event: any, segment: string) {
    if (event.target.checked) {
      this.selectedSegments.push(segment);
    } else {
      this.selectedSegments = this.selectedSegments.filter((s) => s !== segment);
    }
    this.currentPage = 0;
    this.applyFiltersAndSearch();
  }

  toggleAllSegments(event: any) {
    const filtered = this.getFilteredSegments();
    if (event.target.checked) {
      const current = new Set(this.selectedSegments);
      filtered.forEach((s) => current.add(s));
      this.selectedSegments = Array.from(current);
    } else {
      const filteredSet = new Set(filtered);
      this.selectedSegments = this.selectedSegments.filter((s) => !filteredSet.has(s));
    }
    this.currentPage = 0;
    this.applyFiltersAndSearch();
  }

  isAllSegmentsSelected() {
    const filtered = this.getFilteredSegments();
    return (
      filtered.length > 0 &&
      filtered.every((s) => this.selectedSegments.includes(s))
    );
  }

  sortData(sort: Sort): void {
    if (sort && sort.active && sort.direction) {
      this.currentSort = sort;
    } else {
      this.currentSort = { active: 'end_date', direction: 'desc' };
    }
    this.applySort();
    this.paginateData();
  }

  applySort(): void {
    const active = this.currentSort?.active || 'end_date';
    const direction = this.currentSort?.direction || 'desc';
    const isAsc = direction === 'asc';

    this.benchmarkData.sort((a, b) => this.compare(a, b, active, isAsc));
  }

  compare(a: any, b: any, active: string, isAsc: boolean): number {
    const aVal = a ? a[active] : null;
    const bVal = b ? b[active] : null;

    const isEmptyA =
      aVal === null || aVal === undefined || aVal === '' || aVal === '—';
    const isEmptyB =
      bVal === null || bVal === undefined || bVal === '' || bVal === '—';

    if (isEmptyA && isEmptyB) return 0;
    if (isEmptyA) return 1;
    if (isEmptyB) return -1;

    if (active === 'start_date' || active === 'end_date') {
      const timeA = new Date(aVal).getTime();
      const timeB = new Date(bVal).getTime();
      if (!isNaN(timeA) && !isNaN(timeB)) {
        return (timeA - timeB) * (isAsc ? 1 : -1);
      }
      return String(aVal).localeCompare(String(bVal)) * (isAsc ? 1 : -1);
    }

    const numA =
      typeof aVal === 'number'
        ? aVal
        : Number(String(aVal).replace(/,/g, '').trim());
    const numB =
      typeof bVal === 'number'
        ? bVal
        : Number(String(bVal).replace(/,/g, '').trim());

    if (!isNaN(numA) && !isNaN(numB)) {
      return (numA - numB) * (isAsc ? 1 : -1);
    }

    const strA = String(aVal).trim().toLowerCase();
    const strB = String(bVal).trim().toLowerCase();
    return (
      strA.localeCompare(strB, undefined, {
        numeric: true,
        sensitivity: 'base',
      }) * (isAsc ? 1 : -1)
    );
  }

  buildPayload(isDefault: boolean = false): any {
    if (isDefault) {
      return {
        tenant: this.clientNames.map((c) => this.clientTenantMap[c] || c),
        oil_grade: [...this.cargoTypes],
      };
    } else {
      const tenantValues =
        this.selectedClients.length > 0
          ? this.selectedClients.map(
            (c: string) => this.clientTenantMap[c] || c,
          )
          : this.clientNames.map((c) => this.clientTenantMap[c] || c);

      const cargoTypeValues =
        this.selectedCargoTypes.length > 0
          ? this.selectedCargoTypes
          : [...this.cargoTypes];

      return {
        tenant: tenantValues,
        oil_grade: cargoTypeValues,
      };
    }
  }

  loadBenchmarkData(isDefault: boolean = false): void {
    this.isBenchLoading = true;
    this.commonService.fetchBenchmarkData().subscribe({
      next: (response: any) => {
        let rawData = response || [];
        this.fullBenchmarkResponse = rawData;

        const mapped = rawData.map((ship: any) => ({
          vessel_name: ship.vessel_name,
          segment: ship.segment,
          client: ship.client,
          client_name: ship.client,
          voy_no: ship.voyage_no,
          voyage_id: ship.voyage_id,
          operator_remarks: ship.operator_remarks,
          start_port: ship.load_ports,
          start_date: ship.loading_completed,
          end_port: ship.discharge_ports,
          end_date: ship.discharging_completed,
          grade: ship.cargo_sub_type,
          cargo_type: ship.cargo_type,
          qty_mt: ship.cargo_qty,
          load_temp: ship.loading_temp,
          discharge_temp: ship.discharging_temp,
          days_load_port: ship.loading_days,
          days_at_sea: ship.at_sea_days,
          days_disch_port: ship.discharging_days,
          days_total: ship.total_days,
          l_port_fo: ship.loading_fo_cons,
          l_port_go: ship.loading_go_cons,
          at_sea_fo: ship.at_sea_fo_cons,
          at_sea_go: ship.at_sea_go_cons,
          d_port_fo: ship.discharge_fo_cons,
          d_port_go: ship.discharge_go_cons,
          total_fuel_voy: ship.total_fuel_cons,
          benchmark_consp: ship.benchmarking_cons,
          fuel_saving: ship.saving_fuel,
          co2_saving: ship.saving_co2,
        }));

        let filteredData = this.commonService.filterDataByClient(
          mapped,
          this.username,
        );

        if (this.loginPic === 'demo') {
          const vesselMap = new Map<string, string>();
          const clientMap = new Map<string, string>();

          let vesselIndex = 1;
          let clientIndex = 1;

          filteredData = filteredData.map((ship: any) => {
            const originalClient = String(
              ship.client_name || ship.client || '',
            );
            const originalVessel = String(ship.vessel_name || '').trim();
            // Create unique vessel number based on IMO
            if (!vesselMap.has(originalVessel)) {
              vesselMap.set(originalVessel, `Vessel ${vesselIndex++}`);
            }

            // Create unique client number based on client name
            if (!clientMap.has(originalClient)) {
              clientMap.set(originalClient, `Client ${clientIndex++}`);
            }

            const maskedVessel = vesselMap.get(originalVessel)!;
            const maskedClient = clientMap.get(originalClient)!;

            return {
              ...ship,
              ship_imo: 'XXXXXXX',
              vessel_name: maskedVessel,
              client_name: maskedClient,
              client: maskedClient,
            };
          });
        }
        if (this.isBungeUser) {
          this.selectedCargoTypes = ['Veg Oil'];
          filteredData = filteredData.filter((item: any) => {
            const type = (item.cargo_type || '').toString().trim().toLowerCase();
            return type === 'veg oil' || type === 'vegoil';
          });
        }
        this.benchmarkData = filteredData;
        this.allData = [...this.benchmarkData];
        this.clientNames = Array.from(
          new Set(
            this.benchmarkData
              .map((d: any) => (d.client_name || d.client || '').trim())
              .filter(Boolean),
          ),
        ).sort() as string[];
        this.cargoTypes = Array.from(
          new Set(
            this.benchmarkData
              .map((d: any) => (d.cargo_type || '').trim())
              .filter(Boolean),
          ),
        ).sort() as string[];
        this.vesselNames = Array.from(
          new Set(
            this.benchmarkData
              .map((d: any) => (d.vessel_name || '').trim())
              .filter(Boolean),
          ),
        ).sort() as string[];
        this.segmentNames = Array.from(
          new Set(
            this.benchmarkData
              .map((d: any) => (d.segment || '').trim())
              .filter(Boolean),
          ),
        ).sort() as string[];
        this.applySort();
        this.calculateTotals();
        this.updateCharts();
        this.paginateData();
        this.isBenchLoading = false;
      },
      error: () => {
        this.benchmarkData = [];
        this.paginatedData = [];
        this.allData = [];
        this.clientNames = [];
        this.cargoTypes = [];
        this.vesselNames = [];
        this.segmentNames = [];
        this.calculateTotals();
        this.isBenchLoading = false;
      },
    });
  }

  get canOpenBenchmarkReport(): boolean {
    return this.hasClientForBenchmarkReport();
  }

  hasClientForBenchmarkReport(): boolean {
    if (this.isAdminUser) {
      return this.selectedClients.length > 0;
    }
    return !!this.username;
  }

  private getBenchmarkReportClient(): string | null {
    if (this.isAdminUser) {
      return this.selectedClients[0] ?? null;
    }
    const session = this.commonService.getSession();
    return session && session !== 'admin' ? session : this.username;
  }

  openBenchmarkReport(): void {
    if (!this.canOpenBenchmarkReport || this.openingBenchmarkReport) {
      return;
    }

    const client = this.getBenchmarkReportClient();
    if (!client) {
      this.toastr.warning('Please select a client.');
      return;
    }

    this.openingBenchmarkReport = true;
    this.commonService.getBenchmarkReport(client).subscribe({
      next: (response: Blob) => {
        this.openingBenchmarkReport = false;
        const blob =
          response.type === 'application/pdf'
            ? response
            : new Blob([response], { type: 'application/pdf' });
        this.overlayService.showPdf(blob);
      },
      error: () => {
        this.openingBenchmarkReport = false;
        this.toastr.error('Failed to load benchmark report.');
      },
    });
  }

  setPresetView(preset: 'all' | 'compact' | 'days' | 'consumption'): void {
    this.activePreset = preset;
    if (preset === 'all') {
      this.showDaysColumns = true;
      this.showConsumptionColumns = true;
    } else if (preset === 'compact') {
      this.showDaysColumns = false;
      this.showConsumptionColumns = false;
    } else if (preset === 'days') {
      this.showDaysColumns = true;
      this.showConsumptionColumns = false;
    } else if (preset === 'consumption') {
      this.showDaysColumns = false;
      this.showConsumptionColumns = true;
    }
  }

  onTableSearchChange(): void {
    this.currentPage = 0;
    this.applyFiltersAndSearch();
  }

  get commentedRecordsCount(): number {
    if (!this.allData || !this.allData.length) return 0;
    const baseList = this.getBaseFilteredData();
    return baseList.filter((item) => !!this.getOperatorRemarks(item)).length;
  }

  getBaseFilteredData(): any[] {
    if (!this.allData || !this.allData.length) return [];
    const fromDate = this.benchamrkForm?.controls['from_date']?.value;
    const toDate = this.benchamrkForm?.controls['to_date']?.value;

    let filtered = [...this.allData];

    if (this.selectedVessels.length > 0) {
      filtered = filtered.filter((item) =>
        this.selectedVessels.includes(item.vessel_name),
      );
    }

    if (this.selectedSegments.length > 0) {
      filtered = filtered.filter((item) =>
        this.selectedSegments.includes((item.segment || '').trim()),
      );
    }

    if (this.selectedClients.length > 0) {
      filtered = filtered.filter(
        (item) =>
          this.selectedClients.includes(item.client_name) ||
          this.selectedClients.includes(item.client),
      );
    }

    if (this.isBungeUser) {
      this.selectedCargoTypes = ['Veg Oil'];
    }

    if (this.selectedCargoTypes.length > 0) {
      filtered = filtered.filter((item) => {
        const itemType = (item.cargo_type || '').toString().trim().toLowerCase();
        return this.selectedCargoTypes.some((selected) => {
          const selType = (selected || '').toString().trim().toLowerCase();
          if (selType === 'veg oil' || selType === 'vegoil') {
            return itemType === 'veg oil' || itemType === 'vegoil';
          }
          return itemType === selType || item.cargo_type === selected;
        });
      });
    }

    if (fromDate || toDate) {
      const from = fromDate ? new Date(fromDate).getTime() : null;
      const to = toDate ? new Date(toDate).getTime() : null;
      filtered = filtered.filter((item) => {
        const loadDate = new Date(item.end_date).getTime();
        return (!from || loadDate >= from) && (!to || loadDate <= to);
      });
    }

    if (this.tableSearchQuery && this.tableSearchQuery.trim()) {
      const q = this.tableSearchQuery.trim().toLowerCase();
      filtered = filtered.filter((item) => {
        return (
          (item.vessel_name && item.vessel_name.toLowerCase().includes(q)) ||
          (item.segment && item.segment.toLowerCase().includes(q)) ||
          (item.client_name && item.client_name.toLowerCase().includes(q)) ||
          (item.voy_no && String(item.voy_no).toLowerCase().includes(q)) ||
          (item.start_port && item.start_port.toLowerCase().includes(q)) ||
          (item.end_port && item.end_port.toLowerCase().includes(q)) ||
          (item.grade && item.grade.toLowerCase().includes(q))
        );
      });
    }

    return filtered;
  }

  toggleOnlyCommentedRecords(event?: MouseEvent): void {
    if (event) {
      event.stopPropagation();
    }
    this.showOnlyCommentedRecords = !this.showOnlyCommentedRecords;
    this.currentPage = 0;
    this.applyFiltersAndSearch();
  }

  applyFiltersAndSearch(): void {
    let filtered = this.getBaseFilteredData();

    if (this.showOnlyCommentedRecords) {
      filtered = filtered.filter((item) => !!this.getOperatorRemarks(item));
    }

    this.benchmarkData = filtered;
    this.applySort();
    this.calculateTotals();
    this.updateCharts();
    this.paginateData();
  }

  filterClick(): void {
    this.currentPage = 0;
    this.applyFiltersAndSearch();
  }

  clearClick(): void {
    this.isFilterSubmitted = false;
    this.showOnlyCommentedRecords = false;
    this.benchamrkForm.reset();
    this.selectedClients = [];
    this.selectedCargoTypes = this.isBungeUser ? ['Veg Oil'] : [];
    this.selectedVessels = [];
    this.selectedSegments = [];
    this.clientSearchQuery = '';
    this.cargoSearchQuery = '';
    this.vesselSearchQuery = '';
    this.segmentSearchQuery = '';
    this.tableSearchQuery = '';
    this.currentPage = 0;
    this.currentSort = { active: 'end_date', direction: 'desc' };
    this.loadBenchmarkData(true);
    this.updateCharts();
  }

  paginateData(): void {
    const startIndex = this.currentPage * this.itemsPerPage;
    this.paginatedData = this.benchmarkData.slice(
      startIndex,
      startIndex + this.itemsPerPage,
    );
  }

  calculateTotals(): void {
    const data = this.benchmarkData ?? [];

    if (!data.length) {
      this.totalFuelSavingResult = 0;
      this.totalCo2SavingResult = 0;
      this.totalVoyageConsumption = 0;
      this.totalBenchmarkConsumption = 0;
      this.vegOilBenchmarkConsumption = 0;
      this.dppBenchmarkConsumption = 0;
      this.vegOilFuelSavingResult = 0;
      this.vegOilCo2SavingResult = 0;
      this.vegOilRecordsCount = 0;
      this.dppFuelSavingResult = 0;
      this.dppCo2SavingResult = 0;
      this.dppRecordsCount = 0;
      this.totalCargoVolumeMt = 0;
      this.vegOilCargoVolumeMt = 0;
      this.dppCargoVolumeMt = 0;
      this.kpiTiles = this.buildKpiTiles();
      return;
    }

    this.totalFuelSavingResult = data.reduce(
      (sum, item) => sum + (Number(item.fuel_saving) || 0),
      0,
    );
    this.totalCo2SavingResult = data.reduce(
      (sum, item) => sum + (Number(item.co2_saving) || 0),
      0,
    );
    this.totalVoyageConsumption = data.reduce(
      (sum, item) => sum + (parseFloat(item.total_fuel_voy) || 0),
      0,
    );
    this.totalBenchmarkConsumption = data.reduce(
      (sum, item) => sum + (parseFloat(item.benchmark_consp) || 0),
      0,
    );
    this.totalCargoVolumeMt = data.reduce(
      (sum, item) => sum + (Number(item.qty_mt) || 0),
      0,
    );

    const isVegOil = (item: any) => this.isVegOil(item);

    const vegOilData = data.filter(isVegOil);
    this.vegOilRecordsCount = vegOilData.length;

    this.vegOilFuelSavingResult = vegOilData.reduce(
      (sum, item) => sum + (Number(item.fuel_saving) || 0),
      0,
    );

    this.vegOilCo2SavingResult = vegOilData.reduce(
      (sum, item) => sum + (Number(item.co2_saving) || 0),
      0,
    );

    this.vegOilCargoVolumeMt = vegOilData.reduce(
      (sum, item) => sum + (Number(item.qty_mt) || 0),
      0,
    );

    this.vegOilBenchmarkConsumption = vegOilData.reduce(
      (sum, item) => sum + (parseFloat(item.benchmark_consp) || 0),
      0,
    );

    const dppData = data.filter((item) => !isVegOil(item));
    this.dppRecordsCount = dppData.length;

    this.dppFuelSavingResult = dppData.reduce(
      (sum, item) => sum + (Number(item.fuel_saving) || 0),
      0,
    );

    this.dppCo2SavingResult = dppData.reduce(
      (sum, item) => sum + (Number(item.co2_saving) || 0),
      0,
    );

    this.dppCargoVolumeMt = dppData.reduce(
      (sum, item) => sum + (Number(item.qty_mt) || 0),
      0,
    );

    this.dppBenchmarkConsumption = dppData.reduce(
      (sum, item) => sum + (parseFloat(item.benchmark_consp) || 0),
      0,
    );

    this.kpiTiles = this.buildKpiTiles();
  }

  /** Veg Oil records are split out from DPP in the KPIs and the grade column. */
  isVegOil(item: any): boolean {
    const type = (item?.cargo_type || '').toString().trim().toLowerCase();
    return type === 'veg oil' || type === 'vegoil';
  }

  getFilterScopeSummary(): string {
    const parts: string[] = [];
    if (this.selectedClients.length) {
      parts.push(
        this.selectedClients.length === this.clientNames.length
          ? 'All Clients'
          : `${this.selectedClients.length} Clients`,
      );
    }
    if (this.selectedCargoTypes.length) {
      parts.push(`${this.selectedCargoTypes.length} Cargo Types`);
    }
    if (this.selectedVessels.length) {
      parts.push(`${this.selectedVessels.length} Vessels`);
    }
    if (this.selectedSegments.length) {
      parts.push(`${this.selectedSegments.length} Segments`);
    }
    if (
      this.benchamrkForm?.controls['from_date']?.value ||
      this.benchamrkForm?.controls['to_date']?.value
    ) {
      parts.push('Custom Date');
    }
    return parts.length ? parts.join(' • ') : 'Global Fleet Portfolio';
  }

  buildKpiTiles(): BenchmarkKpiTile[] {
    const savingTheme = (v: number): 'positive' | 'negative' => (v >= 0 ? 'positive' : 'negative');
    const tiles: Omit<BenchmarkKpiTile, 'vegPct' | 'dppPct'>[] = [
      {
        key: 'records', title: 'Total Records', icon: 'assessment', theme: 'records',
        unit: '', format: '1.0-0',
        value: this.benchmarkData?.length ?? 0,
        veg: this.vegOilRecordsCount, dpp: this.dppRecordsCount,
      },
      {
        key: 'cargo', title: 'Cargo Carried', icon: 'directions_boat', theme: 'cargo',
        unit: 'MT', format: '1.0-0',
        value: this.totalCargoVolumeMt,
        veg: this.vegOilCargoVolumeMt, dpp: this.dppCargoVolumeMt,
      },
      {
        key: 'fuel', title: 'Fuel Saving', icon: 'local_fire_department',
        theme: savingTheme(this.totalFuelSavingResult),
        unit: 'MT', format: '1.0-2',
        value: this.totalFuelSavingResult,
        veg: this.vegOilFuelSavingResult, dpp: this.dppFuelSavingResult,
        savingPct: this.savingPct(this.totalFuelSavingResult, this.totalBenchmarkConsumption),
        vegSavingPct: this.savingPct(this.vegOilFuelSavingResult, this.vegOilBenchmarkConsumption),
        dppSavingPct: this.savingPct(this.dppFuelSavingResult, this.dppBenchmarkConsumption),
      },
      {
        key: 'co2', title: 'CO₂ Saving', icon: 'eco',
        theme: savingTheme(this.totalCo2SavingResult),
        unit: 'MT', format: '1.0-2',
        value: this.totalCo2SavingResult,
        veg: this.vegOilCo2SavingResult, dpp: this.dppCo2SavingResult,
      },
    ];
    return tiles.map((tile) => ({ ...tile, ...this.kpiShare(tile.veg, tile.dpp) }));
  }

  /** Fuel saving as a percentage of benchmark consumption: (saving / benchmark) × 100. */
  private savingPct(saving: number, benchmark: number): number | null {
    return benchmark > 0 ? ((saving || 0) / benchmark) * 100 : null;
  }

  /** Veg Oil vs DPP share (by magnitude) for the KPI split bar. */
  private kpiShare(veg: number, dpp: number): { vegPct: number; dppPct: number } {
    const total = Math.abs(veg || 0) + Math.abs(dpp || 0);
    if (!total) {
      return { vegPct: 0, dppPct: 0 };
    }
    const vegPct = Math.round((Math.abs(veg || 0) / total) * 100);
    return { vegPct, dppPct: 100 - vegPct };
  }

  trackKpi(_index: number, tile: BenchmarkKpiTile): string {
    return tile.key;
  }

  /** Applies a Light Mode host class so scoped light-theme styles can activate. */
  @HostBinding('class.ch-light') get chLight(): boolean {
    return !this.isDarkMode();
  }

  isDarkMode(): boolean {
    return this.requestFormService.isDarkMode();
  }
  onPageChange(event: any): void {
    this.currentPage = event.pageIndex;
    this.itemsPerPage = event.pageSize;
    this.paginateData();
  }

  generateReport(): void {
    const payload = this.buildPayload();
    const tenants = payload.tenant || [];

    const filteredRawData = this.benchmarkData.map((row: any) => [
      row.vessel_name,
      row.client_name,
      row.voy_no,
      row.start_port,
      row.start_date,
      row.end_port,
      row.end_date,
      row.grade,
      row.qty_mt,
      row.load_temp,
      row.discharge_temp,
      row.days_load_port,
      row.days_at_sea,
      row.days_disch_port,
      row.days_total,
      row.l_port_fo,
      row.l_port_go,
      row.at_sea_fo,
      row.at_sea_go,
      row.d_port_fo,
      row.d_port_go,
      row.total_fuel_voy,
      row.benchmark_consp,
      row.fuel_saving,
      row.co2_saving,
    ]);

    const finalPayload = {
      tenant: tenants,
      data: filteredRawData,
    };

    this.commonService.generatebenchmarkReport(finalPayload).subscribe({
      next: (response: Blob) => {
        const blob = new Blob([response], { type: 'application/pdf' });
        this.overlayService.showPdf(blob);
      },
      error: (error: any) => {
        this.toastr.clear();
        console.error('Error generating report:', error);
      },
    });
  }

  downloadCSV() {
    // Only export currently visible filtered data
    const data = this.benchmarkData;
    if (!data || data.length === 0) return;

    const headers = [
      'S.No',
      'Name',
      'Segment',
      'Client Name',
      'Voy No',
      'Load Port',
      'Completed Load',
      'Discharge Port',
      'Completed Discharge',
      'Grade',
      'Qty (Mt)',
      'L Temp (°C)',
      'D Temp (°C)',
      'Days Load Port',
      'Days At Sea',
      'Days Disch Port',
      'Days Total',
      'L Port FO',
      'L Port GO',
      'At Sea FO',
      'At Sea GO',
      'D Port FO',
      'D Port GO',
      'Total Fuel Consp. On Voy',
      'Bench Mark Consp',
      'Fuel Saving',
      'CO2 Saving',
    ];

    const rows = data.map((row: any, index: number) => [
      index + 1,
      row.vessel_name,
      row.segment,
      row.client_name,
      row.voy_no,
      row.start_port,
      row.start_date,
      row.end_port,
      row.end_date,
      row.grade,
      row.qty_mt,
      row.load_temp,
      row.discharge_temp,
      row.days_load_port,
      row.days_at_sea,
      row.days_disch_port,
      row.days_total,
      row.l_port_fo,
      row.l_port_go,
      row.at_sea_fo,
      row.at_sea_go,
      row.d_port_fo,
      row.d_port_go,
      row.total_fuel_voy,
      row.benchmark_consp,
      row.fuel_saving,
      row.co2_saving,
    ]);

    const csvContent =
      headers.join(',') +
      '\n' +
      rows
        .map((r: any) => r.map((v: any) => `"${v ?? ''}"`).join(','))
        .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'Performance_Benchmarking.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  toggleGraphView(): void {
    if (!this.isAdminUser) {
      return;
    }
    this.showGraphView = !this.showGraphView;
    if (this.showGraphView) {
      this.updateCharts();
    }
  }

  trackBreakdown(index: number, item: any): string {
    return item?.name || index.toString();
  }

  /**
   * Attaches the scroll-sync listeners to one breakdown list (outside Angular, so scrolling
   * never triggers change detection). The list the user is pointing at, touching or focused
   * in becomes the driver; scroll events from the lists it moves are ignored, so the three
   * lists never feed back into each other.
   */
  private bindBreakdownList(key: BreakdownKey, ref: ElementRef<HTMLElement> | undefined): void {
    const el = ref?.nativeElement;
    if (this.breakdownLists[key] === el) {
      return;
    }
    this.breakdownUnbinders[key]?.();
    delete this.breakdownUnbinders[key];
    this.breakdownLists[key] = el;
    if (!el) {
      if (this.activeBreakdown === key) {
        this.activeBreakdown = null;
      }
      return;
    }

    this.zone.runOutsideAngular(() => {
      const claim = () => (this.activeBreakdown = key);
      const onScroll = () => this.syncBreakdownScroll(key);
      const passive = { passive: true };
      el.addEventListener('pointerenter', claim, passive);
      el.addEventListener('wheel', claim, passive);
      el.addEventListener('touchstart', claim, passive);
      el.addEventListener('focusin', claim);
      el.addEventListener('scroll', onScroll, passive);
      this.breakdownUnbinders[key] = () => {
        el.removeEventListener('pointerenter', claim);
        el.removeEventListener('wheel', claim);
        el.removeEventListener('touchstart', claim);
        el.removeEventListener('focusin', claim);
        el.removeEventListener('scroll', onScroll);
      };
    });
  }

  syncBreakdownScroll(sourceName: BreakdownKey): void {
    // Programmatic scrolls on the followers echo back here; only the driver propagates.
    if (this.activeBreakdown && this.activeBreakdown !== sourceName) {
      return;
    }
    const source = this.breakdownLists[sourceName];
    if (!source) {
      return;
    }

    const top = source.scrollTop;
    (Object.keys(this.breakdownLists) as BreakdownKey[]).forEach((key) => {
      const target = this.breakdownLists[key];
      if (key !== sourceName && target && Math.abs(target.scrollTop - top) >= 1) {
        target.scrollTop = top;
      }
    });
  }

  ngOnDestroy(): void {
    (Object.keys(this.breakdownUnbinders) as BreakdownKey[]).forEach((key) => this.breakdownUnbinders[key]?.());
  }

  updateCharts(): void {
    const filtered = this.isAdminUser
      ? this.benchmarkData
      : this.benchmarkData.filter(
        (item) => item.client_name && item.client_name === this.username,
      );

    const clientStats: {
      [key: string]: {
        voyages: number;
        fuelSaving: number;
        co2Saving: number;
        voyageConsp: number;
        benchConsp: number;
      };
    } = {};

    let totalVoyages = 0;
    let totalFuel = 0;
    let totalCo2 = 0;

    filtered.forEach((item) => {
      const client = item.client_name || 'Unknown';
      if (!clientStats[client]) {
        clientStats[client] = { voyages: 0, fuelSaving: 0, co2Saving: 0, voyageConsp: 0, benchConsp: 0 };
      }
      const fuelVal = Number(item.fuel_saving) || 0;
      const co2Val = Number(item.co2_saving) || 0;

      clientStats[client].voyages++;
      clientStats[client].fuelSaving += fuelVal;
      clientStats[client].co2Saving += co2Val;
      clientStats[client].voyageConsp += parseFloat(item.total_fuel_voy) || 0;
      clientStats[client].benchConsp += parseFloat(item.benchmark_consp) || 0;

      totalVoyages++;
      totalFuel += fuelVal;
      totalCo2 += co2Val;
    });

    this.totalVoyagesSummary = totalVoyages;
    this.totalFuelSummary = totalFuel;
    this.totalCo2Summary = totalCo2;

    const isDark = this.isDarkMode();
    const palette = isDark ? CLIENT_PALETTE_DARK : CLIENT_PALETTE_LIGHT;
    const overflowColor = isDark ? CLIENT_OVERFLOW_DARK : CLIENT_OVERFLOW_LIGHT;

    const sortedClientNames = Object.keys(clientStats).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );

    // Colour follows the client (alphabetical slot), so a filter never repaints the rest.
    const clientColorMap: { [key: string]: string } = {};
    sortedClientNames.forEach((clientName, index) => {
      clientColorMap[clientName] = palette[index] ?? overflowColor;
    });

    // 1. Voyage Portfolio Breakdown (Organized Alphabetically)
    const voyageSorted = sortedClientNames.map((name) => ({
      name,
      value: clientStats[name].voyages,
      percentage: totalVoyages > 0 ? (clientStats[name].voyages / totalVoyages) * 100 : 0,
      color: clientColorMap[name],
    }));

    this.voyageBreakdownList = voyageSorted;
    const voyageChartData = voyageSorted.map((item) => ({
      name: item.name,
      y: item.value,
      color: item.color,
    }));

    // 2. Fuel Savings Breakdown (Organized Alphabetically)
    const fuelSorted = sortedClientNames.map((name) => ({
      name,
      value: Number(clientStats[name].fuelSaving.toFixed(2)),
      percentage: totalFuel > 0 ? (Math.max(0, clientStats[name].fuelSaving) / totalFuel) * 100 : 0,
      color: clientColorMap[name],
    }));

    this.fuelBreakdownList = fuelSorted;
    const fuelChartData = fuelSorted.map((item) => ({
      name: item.name,
      y: Math.max(0, item.value),
      color: item.color,
    }));

    // 3. CO2 Savings Breakdown (Organized Alphabetically)
    const co2Sorted = sortedClientNames.map((name) => ({
      name,
      value: Number(clientStats[name].co2Saving.toFixed(2)),
      percentage: totalCo2 > 0 ? (Math.max(0, clientStats[name].co2Saving) / totalCo2) * 100 : 0,
      color: clientColorMap[name],
    }));

    this.co2BreakdownList = co2Sorted;
    const co2ChartData = co2Sorted.map((item) => ({
      name: item.name,
      y: Math.max(0, item.value),
      color: item.color,
    }));

    const ink = {
      surface: isDark ? '#0f1a2e' : '#ffffff',
      text: isDark ? '#e6edf7' : '#0f172a',
      text2: isDark ? '#b4c2d6' : '#475569',
      muted: isDark ? '#8a9bb3' : '#64748b',
      grid: isDark ? 'rgba(148, 163, 184, 0.12)' : '#edf1f6',
      border: isDark ? 'rgba(148, 163, 184, 0.26)' : '#cfd8e3',
    };

    const tooltipBase = {
      backgroundColor: isDark ? 'rgba(15, 26, 46, 0.97)' : 'rgba(255, 255, 255, 0.98)',
      borderColor: ink.border,
      borderRadius: 8,
      borderWidth: 1,
      shadow: false,
      useHTML: true,
      padding: 10,
      style: { color: ink.text, fontSize: '12.5px' },
    };

    const donutTooltip = (unit: string, decimals: number) => ({
      ...tooltipBase,
      headerFormat:
        `<div style="font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:${ink.muted};margin-bottom:4px;">{series.name}</div>`,
      pointFormat:
        '<div style="display:flex;align-items:center;gap:8px;">' +
        '<span style="width:9px;height:9px;border-radius:3px;background:{point.color};display:inline-block;"></span>' +
        `<span style="font-weight:600;color:${ink.text2};">{point.name}</span>` +
        `<span style="margin-left:auto;padding-left:12px;font-weight:700;color:${ink.text};">{point.y:,.${decimals}f} ${unit}</span>` +
        `<span style="color:${ink.muted};font-weight:500;">{point.percentage:.1f}%</span></div>`,
    });

    const donutOptions = (seriesName: string, data: any[], unit: string, decimals: number): any => ({
      chart: {
        type: 'pie',
        backgroundColor: 'transparent',
        height: 220,
        spacing: [4, 4, 4, 4],
        style: { fontFamily: 'inherit' },
      },
      exporting: { enabled: false },
      title: { text: '' },
      credits: { enabled: false },
      tooltip: donutTooltip(unit, decimals),
      plotOptions: {
        pie: {
          innerSize: '74%',
          size: '100%',
          borderWidth: 2,
          borderColor: ink.surface,
          borderRadius: 3,
          dataLabels: { enabled: false },
          states: {
            hover: { halo: { size: 5, opacity: 0.18 }, brightness: 0.04 },
            inactive: { opacity: 0.45 },
          },
        },
      },
      series: [{ name: seriesName, type: 'pie', data }],
    });

    this.voyageChart = new Chart(donutOptions('Voyages', voyageChartData, 'voy', 0));
    this.fuelSavingChart = new Chart(donutOptions('Fuel savings', fuelChartData, 'MT', 1));
    this.co2SavingChart = new Chart(donutOptions('CO₂ reduction', co2ChartData, 'MT', 1));

    // 4. Voyage vs benchmark consumption per client (one axis, two series)
    const consumptionCategories = sortedClientNames;
    const barHeight = Math.max(240, consumptionCategories.length * 46 + 70);

    this.consumptionChart = new Chart({
      chart: {
        type: 'bar',
        backgroundColor: 'transparent',
        height: barHeight,
        spacing: [8, 12, 8, 4],
        style: { fontFamily: 'inherit' },
      },
      exporting: { enabled: false },
      title: { text: '' },
      credits: { enabled: false },
      legend: {
        align: 'right',
        verticalAlign: 'top',
        layout: 'horizontal',
        symbolRadius: 3,
        itemStyle: { color: ink.text2, fontSize: '12px', fontWeight: '600' },
        itemHoverStyle: { color: ink.text },
      },
      xAxis: {
        categories: consumptionCategories,
        lineColor: ink.grid,
        tickLength: 0,
        labels: { style: { color: ink.text2, fontSize: '12px', fontWeight: '600' } },
      },
      yAxis: {
        min: 0,
        title: { text: '' },
        gridLineColor: ink.grid,
        labels: { style: { color: ink.muted, fontSize: '11px' } },
      },
      tooltip: {
        ...tooltipBase,
        shared: true,
        headerFormat:
          `<div style="font-size:11px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:${ink.muted};margin-bottom:4px;">{point.key}</div>`,
        pointFormat:
          '<div style="display:flex;align-items:center;gap:8px;line-height:1.7;">' +
          '<span style="width:9px;height:9px;border-radius:3px;background:{series.color};display:inline-block;"></span>' +
          `<span style="font-weight:600;color:${ink.text2};">{series.name}</span>` +
          `<span style="margin-left:auto;padding-left:14px;font-weight:700;color:${ink.text};">{point.y:,.1f} MT</span></div>`,
      },
      plotOptions: {
        bar: {
          borderWidth: 0,
          borderRadius: 4,
          groupPadding: 0.18,
          pointPadding: 0.06,
          maxPointWidth: 16,
          states: { inactive: { opacity: 0.5 } },
        },
      },
      series: [
        {
          name: 'Voyage consumption',
          type: 'bar',
          color: isDark ? '#3987e5' : '#2a78d6',
          data: consumptionCategories.map((name) => Number(clientStats[name].voyageConsp.toFixed(2))),
        },
        {
          name: 'Benchmark',
          type: 'bar',
          color: isDark ? '#56667e' : '#94a3b8',
          data: consumptionCategories.map((name) => Number(clientStats[name].benchConsp.toFixed(2))),
        },
      ],
    });
  }

  extractOperatorRemarksString(raw: any): string | null {
    if (!raw) {
      return null;
    }
    if (typeof raw === 'string') {
      const trimmed = raw.trim();
      return trimmed.length > 0 ? trimmed : null;
    }
    if (typeof raw === 'object') {
      if (typeof raw.comment === 'string') {
        const trimmed = raw.comment.trim();
        return trimmed.length > 0 ? trimmed : null;
      }
      if (typeof raw.comments === 'string') {
        const trimmed = raw.comments.trim();
        return trimmed.length > 0 ? trimmed : null;
      }
      if (typeof raw.remark === 'string') {
        const trimmed = raw.remark.trim();
        return trimmed.length > 0 ? trimmed : null;
      }
      if (typeof raw.remarks === 'string') {
        const trimmed = raw.remarks.trim();
        return trimmed.length > 0 ? trimmed : null;
      }
      if (Array.isArray(raw)) {
        const joined = raw
          .map((item) => (typeof item === 'string' ? item : item?.comment || item?.comments || item?.remark || item?.remarks || item?.text || ''))
          .filter(Boolean)
          .join('\n')
          .trim();
        return joined.length > 0 ? joined : null;
      }
    }
    return null;
  }

  getOperatorRemarks(row: any): string | null {
    return this.extractOperatorRemarksString(row?.operator_remarks);
  }

  getOperatorRemarksTooltip(row: any): string {
    const rawRemarks = this.extractOperatorRemarksString(row?.operator_remarks);
    if (!rawRemarks) {
      return this.isClientLogin ? 'No remarks recorded' : 'Click to add remarks';
    }

    const points = this.parseRemarksToPoints(rawRemarks)
      .map((p) => p.text.trim())
      .filter((t) => t.length > 0);

    if (!points.length) {
      return this.isClientLogin ? 'No remarks recorded' : 'Click to add remarks';
    }

    const maxPreview = 3;
    const previewPoints = points.slice(0, maxPreview).map((p, idx) => {
      const truncated = p.length > 55 ? `${p.slice(0, 52)}...` : p;
      return points.length > 1 ? `${idx + 1}. ${truncated}` : `• ${truncated}`;
    });

    const remaining = points.length - maxPreview;
    let tooltip = previewPoints.join('\n');
    if (remaining > 0) {
      tooltip += `\n(+${remaining} more · Click to view all)`;
    }
    return tooltip;
  }

  parseRemarksToPoints(raw: any): { id: number; text: string }[] {
    if (Array.isArray(raw)) {
      const arrayPoints = raw
        .map((item, idx) => {
          const t = (typeof item === 'string' ? item : item?.comment || item?.comments || item?.remark || item?.remarks || item?.text || '').trim();
          return t ? { id: Date.now() + idx, text: t.replace(/^([•\-\*]|\d+[\.\)])\s*/, '').trim() } : null;
        })
        .filter((item): item is { id: number; text: string } => item !== null && item.text.length > 0);
      if (arrayPoints.length > 0) {
        return arrayPoints;
      }
    }

    const text = this.extractOperatorRemarksString(raw);
    if (!text) {
      return [];
    }
    const lines = text
      .split(/\r?\n/)
      .map((line) => line.replace(/^([•\-\*]|\d+[\.\)])\s*/, '').trim())
      .filter((line) => line.length > 0);

    return lines.map((lineText, idx) => ({
      id: Date.now() + idx,
      text: lineText,
    }));
  }

  openOperatorRemarksPopup(row: any): void {
    if (!row) return;
    const rawRemarks = this.extractOperatorRemarksString(row?.operator_remarks);

    this.selectedRemarksRow = {
      vessel_name: row.vessel_name || '',
      client_name: row.client_name || row.client || '',
      voy_no: row.voy_no != null ? String(row.voy_no) : '',
      start_port: row.start_port || '',
      end_port: row.end_port || '',
      start_date: row.start_date || '',
      end_date: row.end_date || '',
      remarks: rawRemarks || null,
      targetRow: row,
    };

    const parsed = this.parseRemarksToPoints(row?.operator_remarks);
    this.remarksPoints = parsed.length > 0 ? parsed : [{ id: Date.now(), text: '' }];
    this.showRemarksPopup = true;
    this.adjustAllRemarksTextareas();
  }

  addRemarkPoint(): void {
    this.remarksPoints.push({
      id: Date.now() + Math.floor(Math.random() * 1000),
      text: '',
    });
    this.adjustAllRemarksTextareas();
  }

  removeRemarkPoint(index: number): void {
    this.remarksPoints.splice(index, 1);
    if (this.remarksPoints.length === 0) {
      this.remarksPoints.push({
        id: Date.now(),
        text: '',
      });
    }
    this.adjustAllRemarksTextareas();
  }

  trackRemarkPoint(index: number, item: { id: number; text: string }): number {
    return item.id;
  }

  autoGrowTextarea(target: EventTarget | null): void {
    const el = target as HTMLTextAreaElement;
    if (el) {
      el.style.height = 'auto';
      el.style.height = `${Math.max(el.scrollHeight, 38)}px`;
    }
  }

  adjustAllRemarksTextareas(): void {
    const resizeAll = () => {
      const textareas = document.querySelectorAll<HTMLTextAreaElement>('.remarks-modal-dialog .point-textarea');
      textareas.forEach((el) => {
        el.style.height = 'auto';
        el.style.height = `${Math.max(el.scrollHeight, 38)}px`;
      });
    };

    setTimeout(resizeAll, 0);
    setTimeout(resizeAll, 50);
    setTimeout(resizeAll, 150);
  }

  closeOperatorRemarksPopup(): void {
    if (this.isSavingRemarks) {
      return;
    }
    this.showRemarksPopup = false;
    this.selectedRemarksRow = null;
    this.remarksPoints = [];
  }

  saveOperatorRemarks(): void {
    const target = this.selectedRemarksRow?.targetRow;
    const voyageId = target?.voyage_id;

    if (!voyageId) {
      this.toastr.warning('Voyage ID not found.');
      return;
    }

    const cleanPoints = this.remarksPoints
      .map((p: { id: number; text: string }) => p.text.trim())
      .filter((text: string) => text.length > 0);

    const formattedComment = cleanPoints.join('\n');
    const updatedObj = formattedComment
      ? { comment: formattedComment, remark: formattedComment, remarks: formattedComment }
      : null;

    const payload = {
      voyage_id: voyageId,
      data: {
        comment: formattedComment,
      },
    };

    this.isSavingRemarks = true;
    this.commonService
      .saveBenchmarkOperatorRemarks(payload)
      .subscribe({
        next: () => {
          this.isSavingRemarks = false;
          if (target) {
            target.operator_remarks = updatedObj;
          }
          this.toastr.success('Operator remarks saved successfully.');
          this.closeOperatorRemarksPopup();
          if (this.showOnlyCommentedRecords) {
            this.applyFiltersAndSearch();
          }
        },
        error: (err) => {
          this.isSavingRemarks = false;
          const errMsg = err?.error?.message || err?.message || 'Failed to save operator remarks.';
          this.toastr.error(errMsg);
        },
      });
  }
}
